import { Descriptions, Grid, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type AppointmentModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/AppointmentModuleWithPermissionsSpecs';
import { ContentRefinementSourceAction, ContentRefinementSourceKind } from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import AppointmentModuleAction from './AppointmentModuleAction';
import AppointmentModuleDetailsInner from './AppointmentModuleDetailsInner';
import AppointmentModuleFooter from './AppointmentModuleFooter';
import { HeaderTabs } from './shared';

export type AppointmentModulePageProps = { module: AppointmentModuleWithPermissionsSpecsFragment };

const AppointmentModulePage = ({ module }: AppointmentModulePageProps) => {
    const { t } = useTranslation(['appointmentModuleDetails']);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: permissions.hasUpdatePermission,
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [module.id, permissions.hasUpdatePermission]
    );

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('appointmentModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <ConsolePageWithHeader
                content={content}
                extra={<AppointmentModuleAction module={module} permissions={permissions} />}
                footer={[<AppointmentModuleFooter activeTab={activeTab} />]}
                header={{
                    footer: (
                        <Tabs
                            activeKey={activeTab}
                            items={[
                                {
                                    label: t('appointmentModuleDetails:tabs.mainDetails.title'),
                                    key: HeaderTabs.MainDetails,
                                },
                                {
                                    label: t('appointmentModuleDetails:tabs.email.title'),
                                    key: HeaderTabs.Email,
                                },
                            ]}
                            onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}
                        />
                    ),
                }}
                onBack={() => navigate('/admin/system/modules')}
                title={
                    screens.md ? t('appointmentModuleDetails:title', { name: module.displayName }) : module.displayName
                }
            >
                <AppointmentModuleDetailsInner activeTab={activeTab} module={module} />
            </ConsolePageWithHeader>
        </GenericStateOnPageContextProvider>
    );
};

export default AppointmentModulePage;
